import { Fish, MapPin, AlertCircle, Calendar, Shield } from 'lucide-react'

export interface Spot {
  id: string
  name: string
  distance: string
  rating: number
  species: string[]
  bestTime: string
  facilities: string[]
  difficulty: 'Easy' | 'Moderate' | 'Hard'
  image: string
  description: string
  coordinates: { lat: number; lng: number }
}

export interface Species {
  id: string
  name: string
  season: string
  image: string
  tips: string
}

export interface Rule {
  id: string
  title: string
  summary: string
  details: string
  icon: any
}

export const spots: Spot[] = [
  {
    id: '1',
    name: 'Emerald Lake',
    distance: '2.3 miles',
    rating: 4.8,
    species: ['Rainbow Trout', 'Bass'],
    bestTime: 'Early Morning',
    facilities: ['Parking', 'Restrooms', 'Boat Ramp'],
    difficulty: 'Easy',
    image:
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&q=80&w=1000',
    description:
      'A serene alpine lake perfect for fly fishing. The water is crystal clear and stocked with Rainbow Trout.',
    coordinates: { lat: 45.1, lng: -122.4 },
  },
  {
    id: '2',
    name: 'Whispering River',
    distance: '5.1 miles',
    rating: 4.5,
    species: ['Salmon', 'Steelhead'],
    bestTime: 'Dusk',
    facilities: ['Parking', 'Picnic Area'],
    difficulty: 'Moderate',
    image:
      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&q=80&w=1000',
    description:
      'Fast-moving waters ideal for experienced anglers looking for a challenge. Great for seasonal Salmon runs.',
    coordinates: { lat: 45.2, lng: -122.5 },
  },
  {
    id: '3',
    name: 'Mirror Pond',
    distance: '8.4 miles',
    rating: 4.2,
    species: ['Catfish', 'Bluegill'],
    bestTime: 'Afternoon',
    facilities: ['Restrooms', 'Dock'],
    difficulty: 'Easy',
    image:
      'https://images.unsplash.com/photo-1516939884455-1445c8652f83?auto=format&fit=crop&q=80&w=1000',
    description:
      'Quiet pond accessible for families. Abundant panfish and catfish make for a fun day of catching.',
    coordinates: { lat: 45.15, lng: -122.35 },
  },
  {
    id: '4',
    name: 'Rocky Point',
    distance: '12.0 miles',
    rating: 4.9,
    species: ['Walleye', 'Pike'],
    bestTime: 'Night',
    facilities: ['Parking', 'Camping'],
    difficulty: 'Hard',
    image:
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80&w=1000',
    description:
      'Rugged coastline fishing with deep waters. Requires hiking to reach the best spots.',
    coordinates: { lat: 45.3, lng: -122.6 },
  },
]

export const species: Species[] = [
  {
    id: '1',
    name: 'Rainbow Trout',
    season: 'Spring - Fall',
    image:
      'https://images.unsplash.com/photo-1599488615731-7e5c2823ff28?auto=format&fit=crop&q=80&w=500',
    tips: 'Use spinners or powerbait near the surface in early morning.',
  },
  {
    id: '2',
    name: 'Largemouth Bass',
    season: 'Summer',
    image:
      'https://images.unsplash.com/photo-1535591273668-578e31182c4f?auto=format&fit=crop&q=80&w=500',
    tips: 'Try topwater lures around lily pads and submerged structures.',
  },
  {
    id: '3',
    name: 'Chinook Salmon',
    season: 'Fall',
    image:
      'https://images.unsplash.com/photo-1516478177764-9fe5bd7e9717?auto=format&fit=crop&q=80&w=500',
    tips: 'Use roe or large spoons in deep pools during the run.',
  },
]

export const rules: Rule[] = [
  {
    id: '1',
    title: 'Fishing Licenses',
    summary: 'Required for ages 16+',
    details:
      'Annual or daily licenses must be purchased and carried at all times. Available online or at local retailers.',
    icon: Shield,
  },
  {
    id: '2',
    title: 'Catch Limits',
    summary: 'Daily bag limits apply',
    details:
      'Trout: 5 per day. Bass: 5 per day. Salmon: 2 per day. Check specific waterbody regulations for exceptions.',
    icon: Fish,
  },
  {
    id: '3',
    title: 'Seasonal Closures',
    summary: 'Check dates before you go',
    details:
      'Some streams are closed during spawning seasons (typically winter/early spring) to protect fish populations.',
    icon: Calendar,
  },
  {
    id: '4',
    title: 'Protected Areas',
    summary: 'No fishing zones',
    details:
      'Fishing is prohibited within 100 feet of dams and fish ladders. Respect private property signs.',
    icon: AlertCircle,
  },
]
